import React, {
  useState, useCallback, useContext, useEffect, useRef,
} from 'react';
import PropTypes from 'prop-types';

import useConfigStore from '../../stores/configStore';
import useMapStateStore from '../../stores/mapStateStore';
import { useAppStore } from '../../stores';
import useWindowStore from '../../stores/windowStore';
import useAnchoredModal from '../Modal/useAnchoredModal';
import useTourGuideState from './hooks/useTourGuideState';
import useScrollManagement from './hooks/useScrollManagement';
import useIdleTimeout from './hooks/useIdleTimeout';
import useAbandonBeacon from './hooks/useAbandonBeacon';
import useSessionManagement from './hooks/useSessionManagement';
import useConversationInit from './hooks/useConversationInit';
import useFormSubmit from './hooks/useFormSubmit';
import useAdditionalInterests from './hooks/useAdditionalInterests';
import useButtonClick from './hooks/useButtonClick';

import FavoriteButtonsContext from '../Personalization/Favorite/FavoriteButtonsContext';
import { getSessionScope, loadPersistedSessionId, loadSavedLeadInfo } from './utils/storage';
import { buildMorePlacesMessage, buildNextStepMessage } from './utils/conversationFlow';
import { clearTgaiFavorites } from './utils/helpers';
import { logBotMessage } from './utils/api';

import SlidingPanel from '../SlidingPanel/SlidingPanel';
import CTA from './components/CTA';
import MessageList from './components/MessageList';
import InputBar from './components/InputBar';

import s from './TourGuideAi.module.scss';

const TourGuideAi = ({
  onAmenitySelected, onExternalLocationFound, onResetToCommunity, onEnsureZoomZoneZoom,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { state, dispatch } = useTourGuideState();

  const handleToggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const config = useConfigStore((storeState) => storeState.config);
  const settings = useAppStore((storeState) => storeState.settings);
  const isMobile = useWindowStore((storeState) => storeState.isMobile);

  const { anchorRef, modalStyle, arrowStyle } = useAnchoredModal({
    align: ['right', 'centerY'],
    padding: 45,
    isMobile,
  });

  const scrollManagement = useScrollManagement({
    isOpen, messages: state.messages, isAiThinking: state.isAiThinking,
  });
  const {
    messagesEndRef, setScrollPosition, saveScrollPosition, hasContentBelow, hasContentAbove,
    scrollToBottom, unreadCount,
  } = scrollManagement;

  const { initializeConversation } = useConversationInit({
    state, dispatch, isOpen, setScrollPosition, config,
  });

  const { handleSessionEnd, handleRestartSession } = useSessionManagement({
    state, dispatch, setScrollPosition, onAmenitySelected, config, urlSlug: settings?.urlSlug,
  });

  useAbandonBeacon({ state });

  const { handleFormSubmit } = useFormSubmit({
    state, dispatch, communityNid: config?.communityNid, config,
  });

  const {
    recordActivity, scheduleDeferredMessage, cancelDeferredMessage, flushDeferredMessage,
    handleIdleTimeoutYes, handleIdleTimeoutNo,
  } = useIdleTimeout({
    isOpen,
    state,
    dispatch,
    onSessionEnd: handleSessionEnd,
    communityNid: config?.communityNid,
    communityName: config?.communityName,
  });

  const { handleAdditionalInterestsSubmit } = useAdditionalInterests({
    state,
    dispatch,
    communityNid: config?.communityNid,
    communityName: config?.communityName,
    recordActivity,
    scheduleDeferredMessage,
    onExternalLocationFound,
    onAmenitySelected,
    onResetToCommunity,
    onEnsureZoomZoneZoom,
  });

  const lastBotMessage = [...state.messages].reverse().find((msg) => msg.type === 'bot');
  const lastBotHasButtons = lastBotMessage?.buttons?.length > 0;
  const lastBotIsNextStepPrompt = lastBotMessage?.stepType === 'next_step_prompt'
    || lastBotMessage?.stepType === 'more_places_prompt';

  const showPersistentInput = !state.isSessionEnded && !state.isAiThinking && (
    lastBotIsNextStepPrompt
    || (!lastBotHasButtons && (
      state.conversationState.currentStep === 'step4_followup'
      || state.conversationState.currentStep === 'share_interest'
      || state.conversationState.currentStep === 'step_ask_question'
      || state.hasShownFollowUp
      || state.isAiMode
    ))
  );

  const handleSideNavToggle = () => {
    handleToggle();
  };

  // Emit a favorite_amenity event when a prospect hearts a tgai amenity. Reuses
  // the log-bot-message transport; amenityId is the Drupal place NID, which the
  // backend carries onto the resident's field_favorite_amenities at conversion.
  const handleFavoriteAmenity = useCallback((place, nowFavorited) => {
    logBotMessage(
      `[${nowFavorited ? 'Favorited' : 'Unfavorited'} ${place.title}]`,
      {
        sessionId: state.sessionId,
        communityNid: config?.communityNid,
        questionType: 'favorite_amenity',
        amenityId: place.id,
        amenityName: place.title,
        amenityUuid: place.uuid,
        favorited: nowFavorited,
        userInfo: state.conversationState.userInfo,
      },
      'user',
    );
  }, [state.sessionId, state.conversationState.userInfo, config]);

  // A place modal opened from the interactive map only knows the place uuid and
  // label. Resolve it back to the tgai amenity place (which carries the NID)
  // before emitting, so favorites made in the modal header capture the same way
  // as those toggled on the amenity buttons.
  const handleMapFavoriteAmenity = useCallback((uuid, name, nowFavorited) => {
    const place = state.amenityPlaces.find((p) => String(p.uuid) === String(uuid))
      || { id: undefined, uuid, title: name || '' };
    handleFavoriteAmenity(place, nowFavorited);
  }, [state.amenityPlaces, handleFavoriteAmenity]);

  const { handleButtonClick } = useButtonClick({
    state,
    dispatch,
    config,
    handlers: {
      recordActivity,
      scheduleDeferredMessage,
      cancelDeferredMessage,
      flushDeferredMessage,
      handleSessionEnd,
      handleRestartSession,
      handleIdleTimeoutYes,
      handleIdleTimeoutNo,
      saveScrollPosition,
      scrollToBottom,
      handleSideNavToggle,
      initializeConversation,
      onAmenitySelected,
      onExternalLocationFound,
      onResetToCommunity,
      onEnsureZoomZoneZoom,
    },
  });

  const focusAnimationComplete = useMapStateStore(
    (storeState) => storeState.focusAnimationComplete,
  );
  const amenityModalOpen = useMapStateStore((storeState) => storeState.amenityModalOpen);
  const setTgaiSessionActive = useMapStateStore((storeState) => storeState.setTgaiSessionActive);
  const setEmitFavoriteAmenity = useMapStateStore(
    (storeState) => storeState.setEmitFavoriteAmenity,
  );
  const { pendingExternalVisit } = state.conversationState;

  // Track modal open→close transitions so we only advance after the user
  // actively closes a modal (not on the initial false state).
  const prevAmenityModalOpenRef = useRef(amenityModalOpen);
  useEffect(() => {
    const wasOpen = prevAmenityModalOpenRef.current;
    prevAmenityModalOpenRef.current = amenityModalOpen;
    if (!(wasOpen && !amenityModalOpen)) return;
    if (!state.conversationState.pendingAllVisitedAdvance) return;

    const firstName = state.conversationState.userInfo?.firstName || '';
    dispatch({
      type: 'REPLACE_CONVERSATION_STATE',
      payload: { ...state.conversationState, pendingAllVisitedAdvance: false },
    });
    const logCtx = {
      sessionId: state.sessionId,
      selectedInterests: state.conversationState.selectedInterests?.map((i) => i.uuid) || [],
      userInfo: state.conversationState.userInfo,
      communityNid: config?.communityNid,
    };
    if (state.pendingPlaces && state.pendingPlaces.length > 0) {
      const moreMsg = buildMorePlacesMessage(state.pendingPlacesInterests, firstName, config);
      dispatch({ type: 'APPEND_MESSAGES', payload: [moreMsg] });
      if (moreMsg?.text) {
        logBotMessage(moreMsg.text, { ...logCtx, currentStep: 'more_places', questionType: 'more_places_prompt' });
      }
    } else {
      const nextStep = buildNextStepMessage(firstName, config);
      if (nextStep) {
        dispatch({ type: 'APPEND_MESSAGES', payload: [nextStep] });
        dispatch({ type: 'SET_FLOW_STEP', payload: 7 });
        logBotMessage(nextStep.text, { ...logCtx, currentStep: 'next_step', questionType: 'next_step_prompt' });
      }
    }
  }, [amenityModalOpen]);

  useEffect(() => {
    if (focusAnimationComplete && pendingExternalVisit) {
      const alreadyVisited = state.conversationState.visitedExternalLocations
        ?.includes(pendingExternalVisit);
      dispatch({
        type: 'REPLACE_CONVERSATION_STATE',
        payload: {
          ...state.conversationState,
          visitedExternalLocations: alreadyVisited
            ? state.conversationState.visitedExternalLocations
            : [...(state.conversationState.visitedExternalLocations || []), pendingExternalVisit],
          pendingExternalVisit: null,
        },
      });
    }
  }, [focusAnimationComplete, pendingExternalVisit]);

  const { enableTourGuideAI } = config || {};
  const { platform } = settings || {};
  const showTourGuideAI = enableTourGuideAI && platform === 'siteSee' && !isMobile;

  // Publish tgai session state to the map store so place modals opened from the
  // interactive map surface the favorite heart during a tour (mirroring
  // personalization mode) and report toggles back into the capture pipeline.
  // Both clear on session end and on unmount.
  const tgaiSessionActive = showTourGuideAI && !state.isSessionEnded;
  useEffect(() => {
    setTgaiSessionActive(tgaiSessionActive);
    setEmitFavoriteAmenity(tgaiSessionActive ? handleMapFavoriteAmenity : null);
    return () => {
      setTgaiSessionActive(false);
      setEmitFavoriteAmenity(null);
    };
  }, [tgaiSessionActive, handleMapFavoriteAmenity, setTgaiSessionActive, setEmitFavoriteAmenity]);

  // Wipe one guest's favorites before the next guest's tour starts.
  //
  // The session id is the only thing in the app that marks that boundary: it is
  // resumed for a continuation of the same visit and minted fresh for a new one
  // (see utils/storage and the RESET_SESSION reducer). So the rule is "clear on
  // every session id we did not resume", keyed off the persisted store itself —
  // a guard that lived only as long as the component would be exactly the
  // mismatch that made #1129 look fixed while it leaked.
  const favoriteButtons = useContext(FavoriteButtonsContext);
  const syncFavoritesFromCookies = favoriteButtons?.syncFavoritesFromCookies;
  const resumedSessionIdRef = useRef(undefined);
  if (resumedSessionIdRef.current === undefined) {
    resumedSessionIdRef.current = loadPersistedSessionId(getSessionScope());
  }
  const clearedForSessionRef = useRef(null);
  useEffect(() => {
    if (!showTourGuideAI) return;
    if (state.sessionId === resumedSessionIdRef.current) return;
    if (clearedForSessionRef.current === state.sessionId) return;

    clearedForSessionRef.current = state.sessionId;
    if (clearTgaiFavorites() && syncFavoritesFromCookies) {
      syncFavoritesFromCookies();
    }
  }, [state.sessionId, showTourGuideAI, syncFavoritesFromCookies]);

  // Test hatch: when ?tgai_debug=1 is in the URL, expose setIsOpen and
  // dispatch on window so Playwright specs can open the panel and inject
  // messages without walking the full conversation flow. Only installs when
  // TGAI would actually render — otherwise the hatch would falsely signal
  // "TGAI is mounted" on mobile or when the feature is disabled.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (!showTourGuideAI) return undefined;
    const params = new URLSearchParams(window.location.search);
    if (params.get('tgai_debug') !== '1') return undefined;
    /* eslint-disable no-underscore-dangle -- the __ prefix is the hatch's
       contract with tests/e2e/helpers/tour-guide-ai.ts; renaming breaks it. */
    window.__tgaiSetOpen = setIsOpen;
    window.__tgaiDispatch = dispatch;
    return () => {
      delete window.__tgaiSetOpen;
      delete window.__tgaiDispatch;
    };
    /* eslint-enable no-underscore-dangle */
  }, [dispatch, showTourGuideAI]);

  if (!showTourGuideAI) return null;

  return (
    <>
      <CTA
        panelIsOpen={isOpen}
        onStartTour={handleToggle}
        modalStyle={modalStyle}
        arrowStyle={arrowStyle}
        communityName={config?.communityName}
      />

      <SlidingPanel
        side="right"
        isOpen={isOpen}
        onToggle={handleToggle}
        toggleRef={anchorRef}
        toggleTestId="tgai-panel-toggle"
        className={s.panel}
      >
        <div className={s.tourGuideAi} data-testid="tgai-panel">
          <MessageList
            messages={state.messages}
            formState={{
              isFormSubmitted: state.isFormSubmitted,
              savedLeadInfo: loadSavedLeadInfo(),
            }}
            conversationState={state.conversationState}
            amenityPlaces={state.amenityPlaces}
            isSessionActive={!state.isSessionEnded}
            isAiThinking={state.isAiThinking}
            aiThinkingWithLabel={state.aiThinkingWithLabel}
            messagesEndRef={messagesEndRef}
            hasContentBelow={hasContentBelow}
            hasContentAbove={hasContentAbove}
            onScrollToBottom={scrollToBottom}
            unreadCount={unreadCount}
            handlers={{
              onFormSubmit: handleFormSubmit,
              onButtonClick: handleButtonClick,
              onFavoriteToggle: handleFavoriteAmenity,
            }}
          />
          <InputBar
            value={state.additionalInterests}
            onChange={(val) => dispatch({ type: 'SET_ADDITIONAL_INTERESTS', payload: val })}
            onSubmit={() => handleAdditionalInterestsSubmit()}
            onActivity={recordActivity}
            disabled={!showPersistentInput}
          />
        </div>
      </SlidingPanel>
    </>
  );
};

TourGuideAi.propTypes = {
  onAmenitySelected: PropTypes.func,
  onExternalLocationFound: PropTypes.func,
  onResetToCommunity: PropTypes.func,
  onEnsureZoomZoneZoom: PropTypes.func,
};

TourGuideAi.defaultProps = {
  onAmenitySelected: null,
  onExternalLocationFound: null,
  onResetToCommunity: null,
  onEnsureZoomZoneZoom: null,
};

export default TourGuideAi;
